import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header24 from './components/Header24';
import Footer24 from './components/Footer24';

const About24 = () => {
    const navigate = useNavigate();

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@300;400;500;600;700&display=swap');

                .dhara-about {
                    min-height: 100vh;
                    background: #FFFFFF;
                }

                /* Hero - Full Bleed */
                .dhara-about-hero {
                    position: relative;
                    height: 80vh;
                    min-height: 600px;
                    overflow: hidden;
                }
                .dhara-about-hero-img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .dhara-about-hero-overlay {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.4) 100%);
                    display: flex;
                    align-items: flex-end;
                    padding: 80px;
                }
                .dhara-about-hero-text {
                    max-width: 600px;
                }
                .dhara-about-hero-title {
                    font-family: 'Playfair Display', serif;
                    font-size: 56px;
                    font-weight: 400;
                    color: #FFFFFF;
                    line-height: 1.1;
                    margin-bottom: 20px;
                    letter-spacing: -1px;
                }
                .dhara-about-hero-sub {
                    font-family: 'Inter', sans-serif;
                    font-size: 16px;
                    color: rgba(255,255,255,0.85);
                    font-weight: 300;
                    line-height: 1.7;
                }

                /* Text Section */
                .dhara-about-text-section {
                    max-width: 720px;
                    margin: 0 auto;
                    padding: 120px 40px;
                    text-align: center;
                }
                .dhara-about-section-label {
                    font-family: 'Inter', sans-serif;
                    font-size: 11px;
                    font-weight: 600;
                    letter-spacing: 3px;
                    text-transform: uppercase;
                    color: #00553A;
                    margin-bottom: 24px;
                }
                .dhara-about-section-heading {
                    font-family: 'Playfair Display', serif;
                    font-size: 36px;
                    font-weight: 400;
                    color: #1A1A1A;
                    line-height: 1.3;
                    margin-bottom: 32px;
                    letter-spacing: -0.5px;
                }
                .dhara-about-section-body {
                    font-family: 'Inter', sans-serif;
                    font-size: 16px;
                    color: #6B6B6B;
                    line-height: 1.9;
                    font-weight: 300;
                }

                /* Full-Bleed Image */
                .dhara-about-full-img {
                    width: 100%;
                    height: 60vh;
                    min-height: 400px;
                    object-fit: cover;
                }

                /* Split Section */
                .dhara-about-split {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    min-height: 600px;
                }
                .dhara-about-split-img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    min-height: 500px;
                }
                .dhara-about-split-content {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    padding: 80px;
                }
                .dhara-about-split-content.bg-surface {
                    background: #FAFAF8;
                }

                /* Pull Quote */
                .dhara-about-quote-section {
                    background: #F0F7F4;
                    padding: 100px 40px;
                    text-align: center;
                }
                .dhara-about-quote {
                    font-family: 'Playfair Display', serif;
                    font-size: 32px;
                    font-weight: 400;
                    font-style: italic;
                    color: #00553A;
                    max-width: 700px;
                    margin: 0 auto 24px;
                    line-height: 1.4;
                    letter-spacing: -0.5px;
                }
                .dhara-about-quote-attr {
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    color: #6B6B6B;
                    letter-spacing: 1px;
                }

                /* Founder */
                .dhara-about-founder {
                    display: grid;
                    grid-template-columns: 1fr 1.2fr;
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 120px 40px;
                    gap: 80px;
                    align-items: center;
                }
                .dhara-about-founder-img {
                    width: 100%;
                    aspect-ratio: 3/4;
                    object-fit: cover;
                }
                .dhara-about-founder-name {
                    font-family: 'Playfair Display', serif;
                    font-size: 28px;
                    font-weight: 400;
                    color: #1A1A1A;
                    margin-bottom: 8px;
                }
                .dhara-about-founder-title {
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    color: #00553A;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                    font-weight: 500;
                    margin-bottom: 32px;
                }
                .dhara-about-founder-text {
                    font-family: 'Inter', sans-serif;
                    font-size: 16px;
                    color: #6B6B6B;
                    line-height: 1.9;
                    font-weight: 300;
                    margin-bottom: 20px;
                }

                /* Values Grid */
                .dhara-about-values {
                    background: #FAFAF8;
                    padding: 100px 40px;
                }
                .dhara-about-values-inner {
                    max-width: 1200px;
                    margin: 0 auto;
                }
                .dhara-about-values-title {
                    font-family: 'Playfair Display', serif;
                    font-size: 36px;
                    font-weight: 400;
                    color: #1A1A1A;
                    text-align: center;
                    margin-bottom: 60px;
                    letter-spacing: -0.5px;
                }
                .dhara-about-values-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 48px;
                }
                .dhara-about-value {
                    text-align: center;
                    padding: 40px 24px;
                }
                .dhara-about-value-num {
                    font-family: 'Playfair Display', serif;
                    font-size: 48px;
                    font-weight: 300;
                    color: #00553A;
                    margin-bottom: 16px;
                    opacity: 0.5;
                }
                .dhara-about-value-title {
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    font-weight: 500;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                    color: #1A1A1A;
                    margin-bottom: 16px;
                }
                .dhara-about-value-text {
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #6B6B6B;
                    line-height: 1.8;
                    font-weight: 300;
                }

                /* CTA */
                .dhara-about-cta {
                    text-align: center;
                    padding: 100px 40px 120px;
                }
                .dhara-about-cta-title {
                    font-family: 'Playfair Display', serif;
                    font-size: 36px;
                    font-weight: 400;
                    color: #1A1A1A;
                    margin-bottom: 16px;
                    letter-spacing: -0.5px;
                }
                .dhara-about-cta-text {
                    font-family: 'Inter', sans-serif;
                    font-size: 16px;
                    color: #6B6B6B;
                    font-weight: 300;
                    margin-bottom: 40px;
                }
                .dhara-about-cta-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 12px;
                    padding: 16px 40px;
                    background: #00553A;
                    color: #FFFFFF;
                    border: none;
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    font-weight: 500;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                    cursor: pointer;
                    transition: background 0.3s ease;
                }
                .dhara-about-cta-btn:hover {
                    background: #006B4A;
                }

                @media (max-width: 768px) {
                    .dhara-about-hero { height: 60vh; min-height: 400px; }
                    .dhara-about-hero-overlay { padding: 40px 20px; }
                    .dhara-about-hero-title { font-size: 36px; }
                    .dhara-about-text-section { padding: 80px 20px; }
                    .dhara-about-section-heading { font-size: 28px; }
                    .dhara-about-split { grid-template-columns: 1fr; }
                    .dhara-about-split-content { padding: 48px 20px; }
                    .dhara-about-full-img { height: 40vh; }
                    .dhara-about-quote-section { padding: 60px 20px; }
                    .dhara-about-quote { font-size: 24px; }
                    .dhara-about-founder {
                        grid-template-columns: 1fr;
                        padding: 60px 20px;
                        gap: 40px;
                    }
                    .dhara-about-values { padding: 60px 20px; }
                    .dhara-about-values-grid { grid-template-columns: 1fr; gap: 32px; }
                    .dhara-about-cta { padding: 60px 20px 80px; }
                    .dhara-about-cta-title { font-size: 28px; }
                }
            `}</style>

            <Header24 />

            <main className="dhara-about">
                {/* Hero */}
                <div className="dhara-about-hero">
                    <img
                        className="dhara-about-hero-img"
                        src="https://images.pexels.com/photos/5709661/pexels-photo-5709661.jpeg?auto=compress&cs=tinysrgb&w=1600"
                        alt="Dhara Atelier"
                    />
                    <div className="dhara-about-hero-overlay">
                        <div className="dhara-about-hero-text">
                            <h1 className="dhara-about-hero-title">
                                The Story<br />Behind the Cloth
                            </h1>
                            <p className="dhara-about-hero-sub">
                                Where centuries-old craft meets a singular, modern vision.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Introduction */}
                <div className="dhara-about-text-section">
                    <div className="dhara-about-section-label">Our Philosophy</div>
                    <h2 className="dhara-about-section-heading">
                        We believe clothing should be an act of meaning &mdash;
                        not just consumption
                    </h2>
                    <p className="dhara-about-section-body">
                        Dhara was born from a desire to bridge the worlds of traditional Indian craft
                        and contemporary design. Each piece in our collection begins as a conversation
                        between designer and artisan, between heritage and innovation. We work directly
                        with weaving communities across India, ensuring that every thread carries
                        a story worth telling.
                    </p>
                </div>

                {/* Full-Bleed Image */}
                <img
                    className="dhara-about-full-img"
                    src="https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=1600"
                    alt="Handloom weaving"
                />

                {/* Pull Quote */}
                <div className="dhara-about-quote-section">
                    <p className="dhara-about-quote">
                        &ldquo;Every fabric has a memory. Our work is to honour that memory
                        while giving it a new voice.&rdquo;
                    </p>
                    <p className="dhara-about-quote-attr">
                        &mdash; Priya Mehta, Founder
                    </p>
                </div>

                {/* Split: Craft Philosophy */}
                <div className="dhara-about-split">
                    <img
                        className="dhara-about-split-img"
                        src="https://images.pexels.com/photos/6567340/pexels-photo-6567340.jpeg?auto=compress&cs=tinysrgb&w=1200"
                        alt="Artisan at work"
                    />
                    <div className="dhara-about-split-content bg-surface">
                        <div className="dhara-about-section-label" style={{ textAlign: 'left' }}>The Craft</div>
                        <h2 className="dhara-about-section-heading" style={{ textAlign: 'left', fontSize: '30px' }}>
                            Handmade is not a method.<br />It is a philosophy.
                        </h2>
                        <p className="dhara-about-section-body" style={{ textAlign: 'left' }}>
                            We collaborate with over 40 artisan families across Varanasi, Chanderi,
                            and Bhuj. Our fabrics are woven on pit looms and handlooms using techniques
                            passed down through generations. Each piece takes between 10 and 45 days
                            to create, depending on the complexity of the weave.
                        </p>
                        <p className="dhara-about-section-body" style={{ textAlign: 'left', marginTop: '16px' }}>
                            We pay artisans fair wages above industry standard, and invest in the
                            next generation of weavers through our training initiative.
                        </p>
                    </div>
                </div>

                {/* Founder Portrait */}
                <div className="dhara-about-founder">
                    <img
                        className="dhara-about-founder-img"
                        src="https://images.pexels.com/photos/7679720/pexels-photo-7679720.jpeg?auto=compress&cs=tinysrgb&w=800"
                        alt="Priya Mehta, Founder"
                    />
                    <div>
                        <div className="dhara-about-founder-name">Priya Mehta</div>
                        <div className="dhara-about-founder-title">Founder & Creative Director</div>
                        <p className="dhara-about-founder-text">
                            Priya grew up in a textile family in Ahmedabad. After studying fashion
                            design at NID and working with international fashion houses in London
                            and Paris, she returned to India with a mission: to create a bridge
                            between the extraordinary skill of Indian artisans and the aesthetic
                            language of modern, global design.
                        </p>
                        <p className="dhara-about-founder-text">
                            Dhara is the expression of that mission. Every season, Priya travels
                            to the weaving communities she works with, spending weeks in dialogue
                            about new patterns, techniques, and possibilities. The result is a
                            collection that feels timeless yet entirely of the moment.
                        </p>
                    </div>
                </div>

                {/* Full-Bleed Image */}
                <img
                    className="dhara-about-full-img"
                    src="https://images.pexels.com/photos/8285483/pexels-photo-8285483.jpeg?auto=compress&cs=tinysrgb&w=1600"
                    alt="Dhara collection"
                />

                {/* Values */}
                <div className="dhara-about-values">
                    <div className="dhara-about-values-inner">
                        <h2 className="dhara-about-values-title">What We Stand For</h2>
                        <div className="dhara-about-values-grid">
                            <div className="dhara-about-value">
                                <div className="dhara-about-value-num">01</div>
                                <div className="dhara-about-value-title">Craft First</div>
                                <p className="dhara-about-value-text">
                                    Every design decision begins with the craft. We never compromise
                                    on the integrity of traditional techniques.
                                </p>
                            </div>
                            <div className="dhara-about-value">
                                <div className="dhara-about-value-num">02</div>
                                <div className="dhara-about-value-title">Slow Fashion</div>
                                <p className="dhara-about-value-text">
                                    We produce in small batches, honour the time each piece requires,
                                    and reject the pressure of seasonal overproduction.
                                </p>
                            </div>
                            <div className="dhara-about-value">
                                <div className="dhara-about-value-num">03</div>
                                <div className="dhara-about-value-title">Fair Partnerships</div>
                                <p className="dhara-about-value-text">
                                    Our artisan partners earn above-market wages, share in our success,
                                    and are credited for their extraordinary work.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA */}
                <div className="dhara-about-cta">
                    <h2 className="dhara-about-cta-title">Explore the Collection</h2>
                    <p className="dhara-about-cta-text">
                        Discover pieces that carry stories of heritage and craft.
                    </p>
                    <button
                        className="dhara-about-cta-btn"
                        onClick={() => navigate('/template24/shop')}
                    >
                        Shop Now
                        <ArrowRight size={18} strokeWidth={1.5} />
                    </button>
                </div>
            </main>

            <Footer24 />
        </>
    );
};

export default About24;
